import { FC, useContext, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { CarDriverNoCarDepency } from "../../http/carDrivers/types";
import { getTypesOfUse } from "../../http/cars/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { downloadFileFromData, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import DelegationForm, { DelegationFormBody } from "../cars/DelegationForm";
import SvgDownload from "../icons/Download";
import SvgPin from "../icons/Pin";
import SendMileageRemindersDialog from "../mileages/SendMileageRemindersDialog";
import Button from "../ui/Button";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import CarDriverHistoryDialog from "./CarDriverHistoryDialog";
import CreateCarDriverButton from "./CreateCarDriverButton";
import DeleteCarDriverDialog from "./DeleteCarDriverDialog";
import EditCarDriverDialog from "./EditCarDriverDialog";

const CAR_DRIVER_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "car.carSubsidiary",
  "car.carSubsidiary.id",
  "car.carSubsidiary.subsidiary.id",
  "car.carSubsidiary.subsidiary.allowMultiPlates",
  "startDate",
  "endDate",
  "driver.id",
  "driver.email",
  "driver.fullName",
  "driver.currentCostCenter",
  "typeOfUse",
  "versionNumber",
] as const;
type CarDriverField = (typeof CAR_DRIVER_FIELDS)[number];
type CarDriverSearch = NestedPick<CarDriverNoCarDepency, CarDriverField>;

const CarDrivers: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true, prefix: "cd" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();

  const [itemToDelete, setItemToDelete] = useState<CarDriverSearch>();
  const [itemToEdit, setItemToEdit] = useState<CarDriverSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<CarDriverSearch>();
  const [itemToCreateDelegation, setItemToCreateDelegation] = useState<CarDriverSearch>();
  const [showSendMileageReminders, setShowSendMileageReminders] = useState(false);

  const carDriversApi = useCarDriversApi();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<CarDriverSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_DRIVER_FIELDS,
  };

  const typesOfUse = getTypesOfUse(i18n.t);

  const result = carDriversApi.useAdminCarDrivers(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carDriversApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const onSubmitCarDriverDelegation = async (formData: DelegationFormBody) => {
    if (itemToCreateDelegation) {
      try {
        const docId = await carDriversApi.createDelegation(itemToCreateDelegation.id, itemToCreateDelegation.car.id, {
          language: formData.language.value,
        });
        dispatch(showSuccessNotification(i18n.t));
        setItemToCreateDelegation(undefined);
        navigate(
          `/admin/cars/${itemToCreateDelegation.car.id}?tab=${itemToCreateDelegation.car.carSubsidiary?.subsidiary.allowMultiPlates ? 8 : 7}&doc_id=${docId}`,
        );
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const getRowActions = (item: CarDriverSearch): Action[] => {
    const actions: Action[] = [];
    if (userSettings.permissions.carDrivers.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));
    actions.push({
      title: i18n.t.common.createDelegation,
      onClick: () => setItemToCreateDelegation(item),
      icon: SvgDownload,
    });

    if (userSettings.permissions.carDrivers.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<CarDriverSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: !!userSettings.permissions.carDrivers.insert && <CreateCarDriverButton carId={carId} />,
          secondary: [
            !carId && userSettings.permissions.mileages.insert && userSettings.permissions.subsidiaries.read && (
              <Button buttonProps={{ onClick: () => setShowSendMileageReminders(true) }} glyph={SvgPin}>
                {i18n.t.mileages.sendReminders}
              </Button>
            ),
            <ExportButton.Excel
              onClick={async () => {
                const r = await carDriversApi.exportExcel(query, aborter.current.signal);
                r && downloadFileFromData(r?.data, "Car drivers", r?.contentType);
              }}
            />,
          ],
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="carDriver.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />

                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="carDriver.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th
              title={i18n.t.common.name}
              fieldName="carDriver.driver.fullName"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.email}
              fieldName="carDriver.driver.email"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.costCenter}
              fieldName="carDriver.driver.costCenter"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th title={i18n.t.common.startDate} fieldName="carDriver.startDate" order={orderOptions} />
            <Table.Th title={i18n.t.common.endDate} fieldName="carDriver.endDate" order={orderOptions} />
            <Table.Th
              title={i18n.t.common.typeOfUse}
              fieldName="carDriver.typeOfUse"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => typesOfUse.find((x) => x.value == value)?.label}
            />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                  {!!userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {!!userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!carId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver.id}`}>{item.driver.fullName}</CustomLink>
              ) : (
                item.driver.fullName
              )}
            </td>
            <td>
              <CustomLink to={`mailto:${item.driver.email}`}>
                {item.driver.email}
              </CustomLink>
            </td>
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver.id}`}>{item.driver.currentCostCenter}</CustomLink>
              ) : (
                item.driver.fullName
              )}
            </td>
            <td>{!!item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
            <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
            <td>{typesOfUse.find((x) => x.value == item.typeOfUse)?.label}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteCarDriverDialog
          carDriverId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={() => setItemToDelete(undefined)}
        />
      )}

      {!!itemToEdit && (
        <EditCarDriverDialog carDriverId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} carId={carId} />
      )}

      {!!itemToShowVersions && (
        <CarDriverHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!itemToCreateDelegation && (
        <ModalDialog onClose={() => setItemToCreateDelegation(undefined)} title={i18n.t.common.createDelegation}>
          <DelegationForm onSubmit={onSubmitCarDriverDelegation} carDriverId={itemToCreateDelegation.id} />
        </ModalDialog>
      )}

      {!!showSendMileageReminders && <SendMileageRemindersDialog onClose={() => setShowSendMileageReminders(false)} />}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.carDrivers.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default CarDrivers;
